





























































































































































































import { Vue, Component, PropSync, Ref, Watch } from 'vue-property-decorator'
import { Inject } from 'inversify-props'
import CampoDecimal from '@/components/ui/CampoDecimal.vue'
import Confirmacao from '@/components/ui/Confirmacao.vue'
import SeletorDeCst from '@/components/fiscal/SeletorDeCst.vue'
import SeletorDeClassificacaoTributaria from '@/components/fiscal/SeletorDeClassificacaoTributaria.vue'
import {
	CamposFiscaisManuaisDigitacao,
	ConclusaoDaNotaDeDebito,
	CST,
	DebitoEmitidoDaOrigem,
	ItemDaVenda,
	ItemNotaFiscal,
	OrigemDaNotaDeDebito,
} from '@/models'
import { obrigatorio } from '@/shareds/regras-de-form'
import { formatarMoeda } from '@/shareds/formatadores'
import { findCSTs } from '@/shareds/fiscal'
import {
	montarInformacoesDaNotaDeDebito,
	ratearValorEntreItens,
	somarValoresDoDebito,
	validarCClassTribDaReserva,
	valoresDoDebitoConferem,
} from '@/shareds/fiscal/rateio-nota-de-debito'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import type { NotaFiscalServiceAdapter } from '@/usecases'

const CST_PADRAO_DA_RESERVA = '000'
const CCLASSTRIB_PADRAO_DA_RESERVA = '000001'

@Component({
	components: {
		CampoDecimal,
		Confirmacao,
		SeletorDeCst,
		SeletorDeClassificacaoTributaria,
	},
})
export default class NotaDeDebitoJurosView extends Vue {
	@PropSync('mostra', { type: Boolean, default: false }) syncMostra!: boolean
	@Ref() form!: HTMLFormElement
	@Ref() confirmacaoDeTotalDivergente!: Confirmacao

	@Inject('NotaFiscalServiceAdapter')
	private notaFiscalService!: NotaFiscalServiceAdapter

	obrigatorio = obrigatorio
	formatarMoeda = formatarMoeda
	montarInformacoesDaNotaDeDebito = montarInformacoesDaNotaDeDebito

	chave = ''
	origem: OrigemDaNotaDeDebito | null = null
	juros = 0
	multa = 0
	valores: Record<string, number> = {}
	cstDaReserva: CST | null = null
	cClassTribDaReserva: string | null = CCLASSTRIB_PADRAO_DA_RESERVA
	infoAdicionais = ''
	buscando = false

	headers = [
		{ text: 'Item', value: 'nitem', sortable: false, width: 60 },
		{ text: 'Produto', value: 'produto', sortable: false },
		{ text: 'Quantidade', value: 'qcom', sortable: false, width: 100 },
		{ text: 'Valor na nota', value: 'valorDoItem', sortable: false, width: 130 },
		{ text: 'IBS/CBS', value: 'tributacao', sortable: false, width: 140 },
		{ text: 'Valor do débito', value: 'debito', sortable: false, width: 170 },
	]

	get chaveSemFormatacao() {
		return (this.chave || '').replace(/\D/g, '')
	}

	get modeloDaOrigem() {
		return Number(this.origem?.nota.mod) === 65 ? 'NFC-e' : 'NF-e'
	}

	get destinatarioDaOrigem() {
		if (!this.origem) return ''
		const nota = this.origem.nota
		return nota.xnomeDest || nota.cnpjdest || nota.cpf || this.origem.documentoDoCliente || 'consumidor não identificado'
	}

	get totalInformado() {
		return somarValoresDoDebito([this.juros, this.multa])
	}

	get totalDoDebito() {
		if (!this.origem) return 0
		return somarValoresDoDebito(this.origem.nota.det.map(item => this.valores[item.id]))
	}

	get totalDiverge() {
		return !!this.origem && !valoresDoDebitoConferem(this.totalDoDebito, this.juros, this.multa)
	}

	get itensComDebito() {
		if (!this.origem) return []
		return this.origem.nota.det.filter(item => (Number(this.valores[item.id]) || 0) > 0)
	}

	get reservaEmUso() {
		return this.itensComDebito.some(item => this.usaReserva(item))
	}

	valorDoItem(item: ItemNotaFiscal) {
		return (Number(item.vprod) || 0) - (Number(item.vdesc) || 0)
	}

	usaReserva(item: ItemNotaFiscal) {
		return !!this.origem && this.origem.itensSemIbsCbs.includes(item.id)
	}

	// O CampoDecimal passa para as regras o texto formatado ("3,20"), não o número
	naoNegativo(valor: number | string | null) {
		const numero = typeof valor === 'string'
			? Number(valor.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'))
			: valor
		return !numero || numero >= 0 || 'Informe um valor positivo'
	}

	validarCstDaReserva(cst: CST | null) {
		return !this.reservaEmUso || !!cst || 'Informe o CST de IBS/CBS de reserva'
	}

	validarCClassTribDaReservaNoForm(codigo: string | null) {
		return !this.reservaEmUso || validarCClassTribDaReserva(codigo)
	}

	descreverDebitoEmitido(debito: DebitoEmitidoDaOrigem) {
		const identificacao = debito.numero ? `Nº ${debito.numero} / ${debito.serie || ''}` : 'Sem número'
		const data = debito.dataDeEmissao ? ` em ${new Date(debito.dataDeEmissao).toLocaleDateString('pt-BR')}` : ''
		const situacao = debito.chNFe ? '' : ' (ainda não autorizada)'
		return `${identificacao}: R$ ${formatarMoeda(debito.valor || 0)}${data}${situacao}`
	}

	// O CampoDecimal segura Tab e Enter quando o valor já tem as duas casas: aqui o Tab anda e o Enter não conclui
	aoTeclarNoCampo(evento: KeyboardEvent) {
		if (evento.key === 'Tab') {
			evento.stopPropagation()
			return
		}
		if (evento.key === 'Enter') {
			evento.stopPropagation()
			evento.preventDefault()
		}
	}

	usarValoresRecebidos() {
		if (!this.origem) return
		this.juros = Number(this.origem.jurosRecebidos) || 0
		this.multa = Number(this.origem.multaRecebida) || 0
	}

	@Watch('juros')
	@Watch('multa')
	aoMudarJurosOuMulta() {
		this.ratear()
	}

	// Proporcional ao valor líquido de cada item na nota de origem
	ratear() {
		if (!this.origem) return
		this.valores = ratearValorEntreItens(
			this.totalInformado,
			this.origem.nota.det.map(item => ({ id: item.id, valorBase: this.valorDoItem(item) })),
		)
	}

	async buscarNotaDeOrigem() {
		if (this.chaveSemFormatacao.length !== 44) {
			AlertModule.setError('Informe a chave de acesso da nota da venda, com 44 dígitos.')
			return
		}

		try {
			this.buscando = true
			const origem = await this.notaFiscalService.buscarOrigemDaNotaDeDebito(this.chaveSemFormatacao)

			// A nota de débito referencia nota do mesmo emitente
			const cnpjDaLoja = (VendaModule.lojaDaEntrada?.cnpj || '').replace(/\D/g, '')
			if (cnpjDaLoja && (origem.nota.cnpjemit || '').replace(/\D/g, '') !== cnpjDaLoja) {
				this.origem = null
				AlertModule.setError('A nota da venda foi emitida por outra loja. Selecione a loja emissora da nota original.')
				return
			}

			this.origem = {
				...origem,
				itensSemIbsCbs: origem.itensSemIbsCbs || [],
				debitosEmitidos: origem.debitosEmitidos || [],
			}
			this.juros = 0
			this.multa = 0
			this.valores = origem.nota.det.reduce((valores, item) => ({ ...valores, [item.id]: 0 }), {})
			if (this.origem.itensSemIbsCbs.length && !this.cstDaReserva) await this.carregarCstPadraoDaReserva()
		} catch (error) {
			this.origem = null
			AlertModule.setError(error)
		} finally {
			this.buscando = false
		}
	}

	async carregarCstPadraoDaReserva() {
		try {
			const csts = await findCSTs('IBS')
			this.cstDaReserva = csts.find(cst => cst.codigo === CST_PADRAO_DA_RESERVA) || null
		} catch (error) {
			AlertModule.setError(error)
		}
	}

	async concluir() {
		if (!this.form.validate() || !this.origem) return

		const origem = this.origem
		if (!this.totalInformado) {
			AlertModule.setError('Informe o valor de juros ou de multa recebido.')
			return
		}
		if (!this.itensComDebito.length || !this.totalDoDebito) {
			AlertModule.setError('Informe o valor do débito em pelo menos um item da nota de origem.')
			return
		}
		if (this.totalDiverge) {
			const continuar = await this.confirmacaoDeTotalDivergente.mostrarComParametros(
				'Total diferente de juros mais multa',
				`Os itens somam R$ ${formatarMoeda(this.totalDoDebito)} e juros mais multa somam R$ ${formatarMoeda(this.totalInformado)}. Deseja emitir assim?`,
			)
			if (!continuar) return
		}

		const reserva = this.reservaEmUso && this.cstDaReserva && this.cClassTribDaReserva
			? { cstIBSCBS: this.cstDaReserva, codigoClassificacaoTributaria: this.cClassTribDaReserva.trim() }
			: null

		const conclusao: ConclusaoDaNotaDeDebito = {
			chaveDaOrigem: origem.nota.chNFe,
			documentoDoCliente: origem.documentoDoCliente,
			itens: this.itensComDebito.map(item => criarItemDoDebito(
				item,
				Number(this.valores[item.id]),
				origem.nota.chNFe,
				this.usaReserva(item) ? reserva : null,
			)),
			infoAdicionais: montarInformacoesDaNotaDeDebito(this.juros, this.multa, this.infoAdicionais),
		}

		// Quem fecha o diálogo é a emissão, depois de achar o cliente: sem ele a nota não sai e o operador não perde o rateio
		this.$emit('concluir', conclusao)
	}

	cancelar() {
		this.$emit('update:mostra', false)
	}

	@Watch('syncMostra')
	aoMudarMostra(mostra: boolean) {
		if (!mostra) this.limpar()
	}

	limpar() {
		if (this.form) this.form.resetValidation()
		this.chave = ''
		this.origem = null
		this.juros = 0
		this.multa = 0
		this.valores = {}
		this.cClassTribDaReserva = CCLASSTRIB_PADRAO_DA_RESERVA
		this.infoAdicionais = ''
	}
}

type TributacaoDeReserva = Pick<CamposFiscaisManuaisDigitacao, 'cstIBSCBS' | 'codigoClassificacaoTributaria'>

// Um item por item da nota de origem: quantidade 1 e o valor do débito no preço. O backend monta o XML com os dados do
// item de origem; a tributação de reserva só vai nos itens que não têm IBS/CBS na origem
function criarItemDoDebito(
	item: ItemNotaFiscal,
	valorDoDebito: number,
	chaveDaOrigem: string,
	reserva: TributacaoDeReserva | null,
): ItemDaVenda {
	return {
		id: '',
		produto: item.produto,
		desconto: { isPercentual: true, valor: 0 },
		preco: Number(valorDoDebito.toFixed(2)),
		descontoVarejo: null,
		quantidade: 1,
		quantidadeLida: 0,
		precoDeCusto: 0,
		idConjunto: null,
		percentualCashback: 0,
		diasParaEfetivarCashback: 0,
		isBrinde: false,
		idItemNotaOrigem: item.id,
		chaveNotaOrigem: chaveDaOrigem,
		camposFiscaisDigitacaoManual: reserva ? { ...reserva } as CamposFiscaisManuaisDigitacao : null,
		diasParaExpirarCashback: 0,
		possuiDescontoPorTabela: false,
		valorDescontoRateadoDoItem: 0,
		itemDeDevolucao: false,
		isTotalmenteTrocado: false,
		motivoCancelamento: null,
		isCompra: false,
		idItemOrigem: '',
		isDaVendaReaberta: false,
		vendedor: null,
		autenticadoParaLiberarProdutoSemEstoque: false,
		vendaOrigemDoItem: null,
		valorFreteRateado: 0,
		isItemReferenteATroca: false,
		isDeOutraLoja: false,
		percentualDescontoTabelaPreco: 0,
		precoDeCustoReal: 0,
	} as ItemDaVenda
}
